/**
 * Sales Order Headers filter panel — grouped compact layout matching SO Lines filters.
 */
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Search, X, Filter } from 'lucide-react';
import SearchableSelect from '../../ui/SearchableSelect';

interface FilterOption {
  value: string;
  label: string;
}

export interface POHeadersFilterValues {
  site: string;
  customerGroup: string;
  customer: string;
  commodityGroup: string;
  commodityType: string;
  pdfAttached: string;
}

interface POHeadersFiltersPanelProps {
  filters: POHeadersFilterValues;
  poNumber: string;
  siteOptions: FilterOption[];
  customerGroupOptions: FilterOption[];
  customerOptions: FilterOption[];
  commodityGroupOptions: FilterOption[];
  commodityTypeOptions: FilterOption[];
  activeFilterCount: number;
  onFilterChange: (key: string, value: string) => void;
  onSearch?: () => void;
  onClearFilters: () => void;
  onHideFilters?: () => void;
}

export function POHeadersFiltersPanel({
  filters,
  poNumber,
  siteOptions,
  customerGroupOptions,
  customerOptions,
  commodityGroupOptions,
  commodityTypeOptions,
  activeFilterCount,
  onFilterChange,
  onSearch,
  onClearFilters,
  onHideFilters,
}: POHeadersFiltersPanelProps) {
  const inputCls = 'w-full h-8 px-2 text-sm text-foreground border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary';
  const labelCls = 'text-xs font-medium mb-1 block text-foreground leading-tight';
  const sectionTitleCls = 'text-[11px] font-semibold uppercase tracking-wide text-foreground mb-1.5';
  const sectionCls = 'rounded-md border border-border/50 bg-muted/20 px-2.5 py-2';
  const compactGridCls = 'grid grid-cols-2 gap-x-2 gap-y-1.5';
  const selectCompactCls = '[&>button]:h-8 [&>button]:min-h-8 [&>button]:py-1 [&>button]:px-2 [&>button]:text-sm';

  return (
    <Card className="shadow-sm">
      <CardHeader className="py-2 px-4 border-b border-border/60">
        <CardTitle className="text-sm font-semibold">Filter Sales Orders</CardTitle>
      </CardHeader>
      <CardContent className="px-4 py-3 space-y-2">
        <div className={sectionCls}>
          <div className={sectionTitleCls}>Customer &amp; Site</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-2 gap-y-1.5">
            <div>
              <label className={labelCls}>Company Site</label>
              <SearchableSelect
                options={siteOptions}
                value={filters.site}
                onChange={(val) => onFilterChange('site', val)}
                placeholder="All Company Sites"
                className={selectCompactCls}
              />
            </div>
            <div>
              <label className={labelCls}>Customer Group</label>
              <SearchableSelect
                options={customerGroupOptions}
                value={filters.customerGroup}
                onChange={(val) => onFilterChange('customerGroup', val)}
                placeholder="All Customer Groups"
                className={selectCompactCls}
              />
            </div>
            <div>
              <label className={labelCls}>Customer Site</label>
              <SearchableSelect
                options={customerOptions}
                value={filters.customer}
                onChange={(val) => onFilterChange('customer', val)}
                placeholder="All Customer Sites"
                className={selectCompactCls}
              />
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-2">
          <div className={sectionCls}>
            <div className={sectionTitleCls}>Part &amp; Commodity</div>
            <div className={compactGridCls}>
              <div>
                <label className={labelCls}>Commodity Group</label>
                <select
                  value={filters.commodityGroup}
                  onChange={(e) => onFilterChange('commodityGroup', e.target.value)}
                  className={inputCls}
                >
                  <option value="">All</option>
                  {commodityGroupOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Commodity Type</label>
                <select
                  value={filters.commodityType}
                  onChange={(e) => onFilterChange('commodityType', e.target.value)}
                  className={inputCls}
                >
                  <option value="">All</option>
                  {commodityTypeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className={sectionCls}>
            <div className={sectionTitleCls}>Order &amp; Documents</div>
            <div className={compactGridCls}>
              <div>
                <label className={labelCls}>SO Number</label>
                <input
                  type="text"
                  placeholder="SO Number..."
                  value={poNumber}
                  onChange={(e) => onFilterChange('poNumber', e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') onSearch?.(); }}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>PDF Version Attached</label>
                <select
                  value={filters.pdfAttached}
                  onChange={(e) => onFilterChange('pdfAttached', e.target.value)}
                  className={inputCls}
                >
                  <option value="">All</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border/60">
          <Button type="button" size="sm" className="px-5" onClick={onSearch}>
            <Search className="h-3.5 w-3.5 mr-1.5" />
            Search
          </Button>
          {activeFilterCount > 0 && (
            <Button type="button" variant="outline" size="sm" onClick={onClearFilters} className="px-5">
              <X className="h-3.5 w-3.5 mr-1.5" />
              Clear All
            </Button>
          )}
          {onHideFilters && (
            <Button type="button" variant="outline" size="sm" onClick={onHideFilters}>
              <Filter className="h-3.5 w-3.5 mr-1.5" />
              Hide Filters
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
